import { IntlFormatters } from '@formatjs/intl/src/types';
import * as Sentry from '@sentry/browser';
import {
  AlignmentType,
  Document,
  ExternalHyperlink,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { isEmpty } from 'lodash';

import { groupingContacts, sortGroupedContacts } from 'lib/contacts';
import { filterDeletedValues, getFullName } from 'lib/utils';
import {
  hasDiffDates,
  lessThanPeriodDuration,
  yearsAndMonthsDuration,
} from 'components/Duration/utils';
import { rangeText } from 'components/Range/utils';
import messages from './messages';
import { metrics } from './metrics';
import {
  BaseText,
  characterStyles,
  HeaderH1,
  LINE_HEIGHT,
  MyTable,
  renderTextWithBreaks,
  Space,
  Subheader,
  SubheaderBoldH3,
  SubheaderBoldH3Small,
} from './utils/docxHelpers';
import { getAge, getLocation, getName, getTitleProfile } from './utils/generateDataForDocx';

export const renderPersonalHeaderSection = ({
  profile,
  formatDate,
  formatMessage,
}: {
  formatDate: IntlFormatters['formatDate'];
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
}): Paragraph[] => {
  const isValidAge = profile?.age && profile.age > 0;

  return [
    // Первая строка: ФИО + возраст

    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [
        HeaderH1(getName(profile)),
        Subheader(isValidAge ? `, ${getAge({ profile, formatDate, formatMessage })}` : ''),
      ],
    }),

    // Вторая строка: должность
    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [BaseText(getTitleProfile(profile))],
      spacing: { after: 100, before: 100 },
    }),

    // Третья строка: город
    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [BaseText(getLocation(profile))],
      spacing: { after: 100, before: 100 },
    }),
  ];
};

const renderContactsBlock = (profile: AH$Profile): Table => {
  const PAGE_WIDTH = 12240;
  const HORIZONTAL_MARGINS = 600 * 2;
  const CONTENT_WIDTH = PAGE_WIDTH - HORIZONTAL_MARGINS; // 11040
  const groupedContacts = groupingContacts(profile);
  const sortedGroupedContacts = sortGroupedContacts(groupedContacts);

  const left = Object.keys(sortedGroupedContacts)
    .map((key) => {
      const currentKey = key as keyof typeof sortedGroupedContacts;
      const contacts = sortedGroupedContacts[currentKey];
      const emojiMapped = {
        telegram: '✈️',
        email: '✉️',
        phone: '📞',
        skype: '📱',
      };

      if (!contacts) {
        return [];
      }

      return contacts.map(
        (contact, index) =>
          new Paragraph({
            children: [
              BaseText(!index ? `${emojiMapped[currentKey]} ${contact.value}` : contact.value),
            ],
            ...LINE_HEIGHT,
            indent: { left: !index ? 0 : 360 },
          })
      );
    })
    .flat();

  const rightLinks = (profile.contactsIdByType.get('link') || []).map(
    (_id) => profile?.contactsById?.[_id]
  );

  const rightContent = rightLinks.map(
    (link) =>
      new Paragraph({
        children: [
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: link?.domain || '',
                style: 'Hyperlink',
              }),
            ],
            link: link?.url || '',
          }),
        ],
        ...LINE_HEIGHT,
      })
  );

  return new Table({
    width: {
      size: CONTENT_WIDTH,
      type: WidthType.DXA,
    },
    borders: {
      top: { style: 'none', size: 0, color: 'FFFFFF' },
      bottom: { style: 'none', size: 0, color: 'FFFFFF' },
      left: { style: 'none', size: 0, color: 'FFFFFF' },
      right: { style: 'none', size: 0, color: 'FFFFFF' },
      insideHorizontal: { style: 'none', size: 0, color: 'FFFFFF' },
      insideVertical: { style: 'none', size: 0, color: 'FFFFFF' },
    },
    layout: TableLayoutType.FIXED,
    columnWidths: [CONTENT_WIDTH / 2, CONTENT_WIDTH / 2],
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: CONTENT_WIDTH / 2, type: WidthType.DXA },
            children: left,
          }),
          new TableCell({
            width: { size: CONTENT_WIDTH / 2, type: WidthType.DXA },
            children: rightContent,
          }),
        ],
      }),
    ],
  });
};

export const renderGeneralInformationBlock = ({
  profile,
  formatMessage,
}: {
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
}): Paragraph[] =>
  profile?.displaySummary
    ? [
        new Paragraph({
          children: [Space(), SubheaderBoldH3(formatMessage(messages.generalInfo))],
        }),
        ...renderTextWithBreaks(profile?.displaySummary || ''),
      ]
    : [];

const renderSkills = ({
  skills,
  title,
}: {
  skills: AH$ProfileSkills;
  title: string;
}): Paragraph[] => {
  const renderedSkills = skills.principalSkills.reduce(
    (acc, { skills }) => [...acc, ...skills],
    [] as AH$ProfileSkill[]
  );

  return renderedSkills.length
    ? [
        // Подзаголовок
        new Paragraph({
          children: [Space(), SubheaderBoldH3(title), Space()],
        }),
        new Paragraph({
          children: renderedSkills.map(({ skill }, index) =>
            index === renderedSkills.length - 1 ? BaseText(skill) : BaseText(`${skill}, `)
          ),
          ...LINE_HEIGHT,
        }),
      ]
    : [];
};

const renderWorkExperienceBlock = ({
  profile,
  formatMessage,
}: {
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
}) => {
  const positions = filterDeletedValues(profile.currentPositions.concat(profile.previousPositions));

  const rows = positions.map(
    (
      { position, displayDescription, companyName, startYear, endYear, startMonth, endMonth },
      index
    ) => {
      const { description = '' } = displayDescription[0] || { description: '' };
      const getDurationText = () =>
        hasDiffDates({ endYear, endMonth, startMonth, startYear })
          ? yearsAndMonthsDuration({
              startYear,
              endYear,
              startMonth,
              endMonth,
              isShortFormat: false,
              formatMessage,
            })
          : lessThanPeriodDuration({ startMonth, endMonth, isShortFormat: false, formatMessage });

      const getRangeText = rangeText({
        endYear,
        endMonth,
        startMonth,
        startYear,
        formatMessage,
        format: 'short',
      });

      const pCompanyName = new Paragraph({
        children: index ? [Space(), SubheaderBoldH3(companyName)] : [SubheaderBoldH3(companyName)],
        ...LINE_HEIGHT,
      });
      const pPosition = position
        ? new Paragraph({ children: [SubheaderBoldH3Small(position)], ...LINE_HEIGHT })
        : null;

      const renderRow = [
        pCompanyName,
        new Paragraph({ children: [BaseText(getDurationText()), Space()], ...LINE_HEIGHT }),
        pPosition,
        getRangeText ? new Paragraph({ children: [BaseText(getRangeText)], ...LINE_HEIGHT }) : null,
      ].filter(Boolean) as Paragraph[];
      const renderRowDescription = renderTextWithBreaks(description).map(
        (paragraph) =>
          new TableRow({
            cantSplit: false,
            children: [
              new TableCell({
                width: { size: 720, type: WidthType.DXA },
                children: [],
              }),
              new TableCell({
                children: [paragraph],
              }),
            ],
          })
      );

      return [
        new TableRow({
          cantSplit: false,
          children: [
            new TableCell({
              width: { size: 720, type: WidthType.DXA }, // 0.5 дюйма пустая ячейка
              children: [new Paragraph({})], // пустая
            }),
            new TableCell({
              children: renderRow,
            }),
          ],
        }),
        ...renderRowDescription,
      ];
    }
  );

  return positions?.length
    ? [
        new Paragraph({
          children: [Space(), SubheaderBoldH3(formatMessage(messages.workExperience)), Space()],
          ...LINE_HEIGHT,
        }),
        MyTable({ rows: rows.flat() }),
      ]
    : [];
};

const renderEducationBlock = ({
  profile,
  formatMessage,
}: {
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
}) => {
  const schools = filterDeletedValues(profile?.schools);

  return schools?.length
    ? [
        new Paragraph({
          children: [Space(), SubheaderBoldH3(formatMessage(messages.schools)), Space()],
          ...LINE_HEIGHT,
        }),
        schools.map(({ endMonth, startMonth, startYear, endYear, name, degree }) => {
          const getRangeText = rangeText({
            endYear,
            endMonth,
            startMonth,
            startYear,
            formatMessage,
            format: 'onlyYears',
          });

          return [
            new Paragraph({ children: [SubheaderBoldH3Small(name)], ...LINE_HEIGHT }),
            degree ? new Paragraph({ children: [BaseText(degree)] }) : null,
            getRangeText
              ? new Paragraph({
                  children: [BaseText(getRangeText), Space()],
                  ...LINE_HEIGHT,
                })
              : null,
          ].filter(Boolean) as Paragraph[];
        }),
      ].flat()
    : [];
};

const renderCourses = ({
  profile,
  formatDate,
  formatMessage,
}: {
  formatDate: IntlFormatters['formatDate'];
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
}) => {
  const certificates = filterDeletedValues(profile.certificates);

  return certificates?.length
    ? [
        new Paragraph({
          children: [
            Space(),
            SubheaderBoldH3(formatMessage(messages.coursesAndCertificates)),
            Space(),
          ],
        }),
        certificates.map(({ organization, date, name }) => {
          const currentDate =
            date > 0
              ? new Paragraph({
                  children: [
                    BaseText(
                      formatDate(new Date(date), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    ),
                  ],
                  ...LINE_HEIGHT,
                })
              : null;
          const currentOrganisation = organization
            ? new Paragraph({
                children: [SubheaderBoldH3Small(organization)],
                ...LINE_HEIGHT,
              })
            : null;

          return [
            currentOrganisation,
            currentDate,
            new Paragraph({ children: [BaseText(name)], ...LINE_HEIGHT }),
          ].filter(Boolean) as Paragraph[];
        }),
      ].flat(Infinity)
    : [];
};

const renderLanguage = (profile: AH$Profile) => {
  const languages = profile?.languages || '';

  if (!languages || isEmpty(languages)) {
    return [];
  }

  return Object.keys(languages).map((language) => {
    const level = languages[language] || '';
    const formattedLevel = level ? `${level[0].toUpperCase()}${level.slice(1)}` : '';

    return new Paragraph({
      children: [BaseText(formattedLevel ? `${language} — ${formattedLevel}` : language)],
      ...LINE_HEIGHT,
    });
  });
};

export const downloadDocx = ({
  profile,
  formatDate,
  formatMessage,
  onError,
}: {
  formatDate: IntlFormatters['formatDate'];
  formatMessage: IntlFormatters['formatMessage'];
  profile: AH$Profile;
  onError?: () => void;
}) => {
  const reportError = (error: unknown) => {
    Sentry.withScope((scope) => {
      scope.setTag('feature', 'resume-export');
      scope.setTag('format', 'docx');
      scope.setExtra('profileId', profile.id);
      scope.setExtra('docId', profile.docId);
      Sentry.captureException(error);
    });
    onError?.();
  };

  try {
    const doc = new Document({
      styles: {
        characterStyles,
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 480, // 32px
                bottom: 480, // 32px
                left: 600, // 40px
                right: 600, // 40px
              },
            },
          },
          children: [
            ...renderPersonalHeaderSection({ profile, formatDate, formatMessage }),
            new Paragraph({
              children: [Space(), SubheaderBoldH3(formatMessage(messages.contacts)), Space()],
            }),
            renderContactsBlock(profile),
            ...renderGeneralInformationBlock({ profile, formatMessage }),
            ...renderSkills({
              skills: profile.skills,
              title: formatMessage(messages.relevantSkills),
            }),
            ...renderWorkExperienceBlock({ profile, formatMessage }),
            ...renderEducationBlock({ profile, formatMessage }).flat(),
            ...(renderCourses({ profile, formatDate, formatMessage }) as Paragraph[]),
            new Paragraph({
              children: [Space(), SubheaderBoldH3(formatMessage(messages.languages)), Space()],
            }),
            ...renderLanguage(profile),
          ],
        },
      ],
    });
    const fileName = getFullName(profile, '_');

    Packer.toBlob(doc)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = `${fileName}.docx`;
        // ! Для старых Linux/Firefox
        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        metrics.downloadDocx();
      })
      .catch(reportError);
  } catch (error) {
    reportError(error);
  }
};
